// HTTP Client centralizado
// Padrão determinístico: exige NEXT_PUBLIC_API_BASE_URL obrigatória

import { clearToken, getToken } from "../lib/auth";
import { isEndedAdminSessionError } from "../lib/sessionErrors";

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
export type Query = Record<string, string | number | boolean | undefined | null>;

// ========================================
// Base URL (Obrigatória)
// ========================================

const ENV_BASE = process.env.NEXT_PUBLIC_API_BASE_URL;

if (!ENV_BASE) {
  throw new Error(
    "❌ NEXT_PUBLIC_API_BASE_URL não está definida.\n" +
      "Defina no .env.local (dev) ou nas variáveis de ambiente da produção."
  );
}

export const API_BASE_URL = ENV_BASE.replace(/\/+$/, "");

// ========================================
// Helpers
// ========================================

function buildUrl(path: string, query?: Query) {
  const cleanPath = path.replace(/^\/+/, "");
  const url = new URL(cleanPath, API_BASE_URL + "/");

  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.set(key, String(value));
      }
    });
  }

  return url.toString();
}

function getJsonHeaders(token?: string): HeadersInit {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  return headers;
}

async function forceAdminLogout(reason: string) {
  await clearToken();

  if (typeof window === "undefined") return;

  const currentPath = window.location.pathname || "";
  if (!currentPath.startsWith("/login")) {
    window.location.assign(`/login?reason=${encodeURIComponent(reason)}`);
  }
}

async function fetchWithTimeout(url: string, options: RequestInit, timeout = 30000) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    return await fetch(url, {
      credentials: "include", // enviar cookies (para middleware ou auth baseada em cookie)
      ...options,
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timeoutId);
  }
}

// ========================================
// HTTP Core
// ========================================

export async function http<T>(
  path: string,
  options?: {
    method?: HttpMethod;
    body?: any;
    token?: string;
    query?: Query;
    cache?: RequestCache;
  }
): Promise<T> {
  const method = options?.method || "GET";
  const url = buildUrl(path, options?.query);

  if (process.env.NODE_ENV === "development") {
    console.log(`[HTTP] ${method} → ${url}`);
  }

  let response: Response;

  try {
    response = await fetchWithTimeout(url, {
      method,
      headers: getJsonHeaders(options?.token),
      body: options?.body !== undefined ? JSON.stringify(options.body) : undefined,
      cache: options?.cache ?? "no-store",
    });
  } catch (error: any) {
    let message = "Falha de conexão com o servidor";
    let code = "NETWORK_ERROR";

    if (error?.name === "AbortError") {
      message = "Tempo limite da requisição excedido";
      code = "TIMEOUT";
    }

    const err: any = new Error(message);
    err.status = 0;
    err.code = code;
    err.cause = error;

    throw err;
  }

  const contentType = response.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");

  let data: any = null;

  try {
    data = isJson ? await response.json() : await response.text();
  } catch {
    data = null;
  }

  if (!response.ok) {
    const code = typeof data?.code === "string" ? data.code : "";

    if (code === "EMPLOYEE_SUBSCRIPTION_ACCESS_BLOCKED") {
      await forceAdminLogout("assinatura-inativa");
    } else if (
      options?.token &&
      isEndedAdminSessionError({ status: response.status, data }) &&
      options.token === getToken()
    ) {
      // Sessão revogada ou usuário inativo: só derruba a sessão admin quando
      // foi o token admin que o servidor recusou (o do cliente tem fluxo próprio).
      await forceAdminLogout("sessao-encerrada");
    }

    const message = mapErrorMessage({
      status: response.status,
      statusText: response.statusText,
      data,
      path,
    });

    const err: any = new Error(message);
    err.status = response.status;
    err.data = data;

    if (isJson && data?.code) {
      err.code = data.code;
    }

    throw err;
  }

  return data as T;
}

// ========================================
// Error Mapping
// ========================================

/**
 * Mensagem do backend que pode ir para a tela. Erro de validação antigo vinha
 * como o JSON do zod serializado (`parse.error.message`); isso nunca é exibido
 * (D-02). Nesse caso a mensagem é tratada como ausente e cada status cai na
 * frase padrão em pt-BR.
 */
export function toReadableApiMessage(value: unknown): string {
  if (typeof value !== "string") return "";
  const message = value.trim();
  if (!message) return "";
  if (message.startsWith("[") || message.startsWith("{")) {
    try {
      const parsed: unknown = JSON.parse(message);
      if (parsed !== null && typeof parsed === "object") return "";
    } catch {
      // Não é JSON: texto que só começa com colchete ou chave.
    }
  }
  return message;
}

function isLoginPath(normalizedPath: string): boolean {
  return (
    normalizedPath.startsWith("auth/login") ||
    normalizedPath.startsWith("customer-auth/login")
  );
}

export function mapHttpErrorMessage(params: {
  status: number;
  statusText?: string;
  data: any;
  path: string;
}): string {
  const { status, statusText, data, path } = params;

  const code = typeof data?.code === "string" ? data.code : "";
  const message = toReadableApiMessage(data?.message);
  const normalizedPath = path.replace(/^\/+/, "");

  const functionalMessages: Record<string, string> = {
    RECIPIENT_SITE_URL_INVALID: "Informe uma URL pública completa e válida.",
    RECIPIENT_ADDRESS_DECLARATION_REQUIRED:
      "Informe o complemento e o ponto de referência ou confirme que não existem.",
    RECIPIENT_LEGAL_REPRESENTATIVE_REQUIRED:
      "Confirme quem é o representante legal da empresa.",
    RECIPIENT_ALREADY_CONNECTED: "Este estabelecimento já possui um recebedor conectado.",
    RECIPIENT_PROVIDER_REJECTED:
      "A Stone recusou os dados informados. Revise o formulário e tente novamente.",
    RECIPIENT_PROVIDER_UNAVAILABLE:
      "Não foi possível acessar o recebedor agora. Tente novamente em alguns instantes.",
    RECIPIENT_UPDATE_NOT_CONFIRMED:
      "A atualização ainda não foi confirmada. Tente novamente.",
    PAYMENT_HISTORY_SYNC_PENDING: "O histórico financeiro ainda está sendo sincronizado.",
    PAYMENT_PROVIDER_UNAVAILABLE:
      "O serviço de pagamentos está indisponível no momento. Tente novamente.",
    PAYMENT_TRANSFER_SCOPE_MISMATCH:
      "Esta transferência não pertence ao recebedor selecionado.",
    // Módulo WhatsApp: só o código próprio do módulo entra aqui. Os genéricos
    // (VALIDATION_ERROR, SUBSCRIPTION_REQUIRED, ESTABLISHMENT_DEACTIVATED) são
    // usados pelo backend inteiro e já têm tratamento por status.
    WHATSAPP_SESSION_NOT_CONNECTED:
      "Conecte o WhatsApp da unidade antes de enviar mensagens.",
    ONLINE_PAYMENT_DISABLED:
      "O pagamento online foi desativado neste estabelecimento. Fale com ele para combinar outra forma de pagar.",
    SUPPORT_SESSION_READ_ONLY:
      "Sessão de suporte é só para consulta. Para alterar algo, peça ao cliente.",
    DEBIT_TAX_NOT_CONFIGURED:
      "Não há uma taxa ativa de débito configurada neste ambiente.",
    DEBIT_TAX_INCOMPLETE: "A taxa de débito está incompleta neste ambiente.",
    CARD_TAX_NOT_CONFIGURED:
      "Não há uma taxa ativa para este prazo de crédito neste ambiente.",
    CARD_TAX_INCOMPLETE: "A taxa de crédito está incompleta neste ambiente.",
    PIX_TAX_NOT_CONFIGURED: "Não há uma taxa ativa de Pix configurada neste ambiente.",
  };

  if (functionalMessages[code]) return functionalMessages[code];

  if (status === 0) {
    return "Falha de conexão com o servidor";
  }

  if (status === 401) {
    if (isLoginPath(normalizedPath)) {
      return "E-mail ou senha incorretos";
    }
    if (normalizedPath.startsWith("auth/change-password")) {
      return "Senha atual incorreta";
    }
    return message || "Você precisa estar autenticado";
  }

  if (status === 403) {
    if (code === "ESTABLISHMENT_DEACTIVATED") {
      return (
        message ||
        "Esta unidade está desativada no momento. Selecione outra unidade ativa."
      );
    }
    if (code === "SUBSCRIPTION_REQUIRED") {
      return "Sua assinatura está inativa. Regularize para continuar";
    }
    return message || "Você não tem permissão para esta ação";
  }

  if (status === 404) {
    return message || "Recurso não encontrado";
  }

  if (status === 409) {
    if (code === "APPOINTMENT_CONFLICT") return "Horário já reservado";
    if (code === "BILLING_NO_ACTIVE_SUBSCRIPTION") {
      return (
        message ||
        "Não há assinatura ativa no momento. Vá para Assinatura para contratar ou reativar um plano."
      );
    }
    if (code === "SUBSCRIPTION_PAYMENT_PROCESSING") {
      return (
        message ||
        "Seu pagamento está em análise. Aguarde alguns instantes e atualize a área do cliente."
      );
    }
    return message || "Conflito de dados";
  }

  if (status === 422) {
    if (/the request is invalid/i.test(message)) {
      return "Dados do pagamento inválidos. Verifique número do cartão, validade, CVV e endereço.";
    }
    return message || "Não foi possível processar a solicitação";
  }

  if (status === 402) {
    if (code === "PIX_PAYMENT_FAILED") {
      return (
        message ||
        "Não foi possível gerar o PIX agora. Tente cartão ou escolha outro método de pagamento."
      );
    }
    if (code === "SUBSCRIPTION_PAYMENT_FAILED") {
      return (
        message ||
        "Pagamento não aprovado. Verifique os dados do cartão ou tente outro método."
      );
    }
    return message || "Pagamento não aprovado";
  }

  if (status === 400) {
    if (/the request is invalid/i.test(message)) {
      return "Dados da solicitação inválidos. Revise as informações e tente novamente.";
    }
    if (!message && isLoginPath(normalizedPath)) {
      // PUB-05: senha curta ou e-mail fora do formato também não entram.
      return "E-mail ou senha incorretos";
    }
    return message || "Dados inválidos";
  }

  if (status === 429) {
    return message || "Muitas tentativas. Aguarde alguns minutos e tente novamente.";
  }

  if (status >= 500) {
    return "Erro interno do servidor. Tente novamente";
  }

  return message || statusText || "Erro inesperado";
}

const mapErrorMessage = mapHttpErrorMessage;

// ========================================
// URL helper for file uploads and forms
// ========================================

/**
 * Retorna a URL completa para o caminho dado, preservando a base da API.
 * Usado por serviços que precisam postar `FormData` e não podem usar `http()`.
 */
export function buildFormUrl(path: string) {
  const cleanPath = path.replace(/^\/+/, "");
  return new URL(cleanPath, API_BASE_URL + "/").toString();
}
